import { forwardRef } from 'react';

import type { Tone } from './types';

type BadgeVariant = Extract<Tone, 'neutral' | 'success' | 'warning' | 'danger' | 'info'>;

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  leadingDot?: boolean;
  leadingIcon?: React.ReactNode;
}

const badgeVariantClasses: Record<BadgeVariant, string> = {
  neutral:
    'bg-[color:var(--color-severity-info-muted-bg)] text-[color:var(--color-severity-info-muted-fg)] border-[color:var(--color-severity-info-border)]',
  success:
    'bg-[color:var(--color-status-resolved-bg)] text-[color:var(--color-status-resolved-fg)] border-[color:var(--color-status-resolved-solid)]/35',
  warning:
    'bg-[color:var(--color-sla-at-risk-bg)] text-[color:var(--color-sla-at-risk-fg)] border-[color:var(--color-sla-at-risk-solid)]/35',
  danger:
    'bg-[color:var(--color-sla-breached-bg)] text-[color:var(--color-sla-breached-fg)] border-[color:var(--color-sla-breached-solid)]/35',
  info:
    'bg-[color:var(--color-status-new-bg)] text-[color:var(--color-status-new-fg)] border-[color:var(--color-status-new-solid)]/35',
};

const badgeDotClasses: Record<BadgeVariant, string> = {
  neutral: 'bg-[color:var(--color-severity-info-solid)]',
  success: 'bg-[color:var(--color-status-resolved-solid)]',
  warning: 'bg-[color:var(--color-sla-at-risk-solid)]',
  danger: 'bg-[color:var(--color-sla-breached-solid)]',
  info: 'bg-[color:var(--color-status-new-solid)]',
};

const sizeClasses = {
  sm: {
    root: 'h-5 px-2 py-0.5 text-xs',
    dot: 'h-1.5 w-1.5',
  },
  md: {
    root: 'h-6 px-2.5 py-0.5 text-sm',
    dot: 'h-2 w-2',
  },
} as const;

function cn(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(' ');
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  {
    variant = 'neutral',
    size = 'md',
    leadingDot = false,
    leadingIcon,
    className,
    children,
    ...rest
  },
  ref,
) {
  const classes = sizeClasses[size];

  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full border font-medium',
        classes.root,
        badgeVariantClasses[variant],
        className,
      )}
      {...rest}
    >
      {leadingIcon}
      {leadingDot ? <span aria-hidden="true" className={cn('rounded-full', classes.dot, badgeDotClasses[variant])} /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
});
